import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type {
  ItemBatchPreview,
  ItemBulkOperation,
  ItemListEntry,
  ItemFilterField,
  ItemFilterOperator,
  ItemFilterRule,
  ItemFilterSet,
  ItemListColumn,
  ItemListPreference,
  ItemListStats,
  ReferenceData,
} from '../../shared/types';
import { DEFAULT_ITEM_LIST_COLUMNS, ITEM_LIST_COLUMN_IDS } from '../../shared/types';
import { api } from '../api';
import { formatDate, ingameStatusLabel, statusLabel } from '../utils';

const emptyStats: ItemListStats = { loaded: 0, approved: 0, review: 0, errors: 0, updateRequired: 0, pending: 0, synced: 0, deletionPending: 0, deletionErrors: 0, ingameDeleted: 0 };
const pageSizes: ItemListPreference['pageSize'][] = [10, 25, 50, 100];

type FilterKind = 'text' | 'enum' | 'number' | 'date' | 'multi' | 'boolean';
type UiFilterRule = ItemFilterRule & { id: string };

type FilterFieldDefinition = {
  id: ItemFilterField;
  label: string;
  kind: FilterKind;
  options?: { key: string; label: string }[];
};

const columnLabels: Record<ItemListColumn, string> = {
  item: 'Item',
  technicalKey: 'Technischer Schlüssel',
  description: 'Beschreibung',
  rarity: 'Seltenheit',
  category: 'Kategorie',
  subcategory: 'Unterkategorie',
  contentStatus: 'Freigabestatus',
  ingameStatus: 'Ingame-Status',
  tags: 'Tags',
  origins: 'Herkunft',
  maximumStack: 'Stapelgröße',
  buyPrice: 'Einkauf',
  sellPrice: 'Verkauf',
  revision: 'Revision',
  iconStatus: 'Icon',
  updatedAt: 'Geändert',
  createdAt: 'Erstellt',
  ingameRevision: 'Ingame-Revision',
  ingameAssetPath: 'Ingame-Asset',
  ingameLastSucceededAt: 'Letzte Übernahme',
};

const operatorLabels: Record<ItemFilterOperator, string> = {
  contains: 'Enthält',
  not_contains: 'Enthält nicht',
  equals: 'Ist gleich',
  not_equals: 'Ist ungleich',
  starts_with: 'Beginnt mit',
  ends_with: 'Endet mit',
  greater_than: 'Größer als',
  greater_or_equal: 'Größer oder gleich',
  less_than: 'Kleiner als',
  less_or_equal: 'Kleiner oder gleich',
  before: 'Liegt vor',
  after: 'Liegt nach',
  on: 'Ist am',
  is_empty: 'Ist leer',
  is_not_empty: 'Ist nicht leer',
  is_set: 'Ist vorhanden',
  is_not_set: 'Fehlt',
};

const operatorsByKind: Record<FilterKind, ItemFilterOperator[]> = {
  text: ['contains', 'not_contains', 'equals', 'not_equals', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty'],
  enum: ['equals', 'not_equals', 'is_empty', 'is_not_empty'],
  number: ['equals', 'not_equals', 'greater_than', 'greater_or_equal', 'less_than', 'less_or_equal', 'is_empty', 'is_not_empty'],
  date: ['on', 'before', 'after', 'is_empty', 'is_not_empty'],
  multi: ['contains', 'not_contains', 'is_empty', 'is_not_empty'],
  boolean: ['is_set', 'is_not_set'],
};

const valueFreeOperators = new Set<ItemFilterOperator>(['is_empty', 'is_not_empty', 'is_set', 'is_not_set']);

function makeRule(field: ItemFilterField = 'name', definitions?: FilterFieldDefinition[]): UiFilterRule {
  const definition = definitions?.find((entry) => entry.id === field);
  const kind = definition?.kind ?? 'text';
  const operator = operatorsByKind[kind][0];
  const value = definition?.options?.[0]?.key ?? '';
  return { id: crypto.randomUUID(), field, operator, value };
}

function formatNumber(value: number) {
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(value);
}

export function ItemListPage({
  reference,
  onOpen,
  onNew,
  onDuplicate,
  onMessage,
}: {
  reference: ReferenceData;
  onOpen: (id: string) => void;
  onNew: () => void;
  onDuplicate: (id: string) => void;
  onMessage: (kind: 'success'|'error', message: string) => void;
}) {
  const [items, setItems] = useState<ItemListEntry[]>([]);
  const [stats, setStats] = useState<ItemListStats>(emptyStats);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [ingameState, setIngameState] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<ItemListPreference['pageSize']>(25);
  const [visibleColumns, setVisibleColumns] = useState<ItemListColumn[]>([...DEFAULT_ITEM_LIST_COLUMNS]);
  const [preferencesReady, setPreferencesReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [draftMatch, setDraftMatch] = useState<ItemFilterSet['match']>('all');
  const [draftRules, setDraftRules] = useState<UiFilterRule[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<ItemFilterSet>({ match: 'all', rules: [] });
  const requestSequence = useRef(0);
  const importFileRef = useRef<HTMLInputElement>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [importOpen, setImportOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importCsv, setImportCsv] = useState('');
  const [importFileName, setImportFileName] = useState('');
  const [importPreview, setImportPreview] = useState<ItemBatchPreview | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkOperation, setBulkOperation] = useState<ItemBulkOperation>('add_tag');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const filterDefinitions = useMemo<FilterFieldDefinition[]>(() => {
    const subcategories = reference.categories.flatMap((category) => category.subcategories.map((subcategory) => ({
      key: subcategory.key,
      label: `${category.label} › ${subcategory.label}`,
    })));
    const tags = reference.tagGroups.flatMap((group) => group.tags.map((tag) => ({
      key: tag.key,
      label: `${group.label} › ${tag.label}`,
    })));

    return [
      { id: 'name', label: 'Name (Deutsch)', kind: 'text' },
      { id: 'description', label: 'Beschreibung (Deutsch)', kind: 'text' },
      { id: 'englishName', label: 'Name (Englisch)', kind: 'text' },
      { id: 'englishDescription', label: 'Beschreibung (Englisch)', kind: 'text' },
      { id: 'technicalKey', label: 'Technischer Schlüssel', kind: 'text' },
      { id: 'status', label: 'Freigabestatus', kind: 'enum', options: reference.statuses },
      { id: 'rarity', label: 'Seltenheit', kind: 'enum', options: reference.rarities.map(({ key, label }) => ({ key, label })) },
      { id: 'category', label: 'Kategorie', kind: 'enum', options: reference.categories.map(({ key, label }) => ({ key, label })) },
      { id: 'subcategory', label: 'Unterkategorie', kind: 'enum', options: subcategories },
      { id: 'tag', label: 'Tag', kind: 'multi', options: tags },
      { id: 'origin', label: 'Herkunft', kind: 'multi', options: reference.origins },
      { id: 'buyPrice', label: 'Einkaufspreis', kind: 'number' },
      { id: 'sellPrice', label: 'Verkaufspreis', kind: 'number' },
      { id: 'maximumStack', label: 'Maximaler Stapel', kind: 'number' },
      { id: 'revision', label: 'Content-Revision', kind: 'number' },
      { id: 'hasIcon', label: 'Item-Icon', kind: 'boolean' },
      {
        id: 'ingameState',
        label: 'Ingame-Status',
        kind: 'enum',
        options: [
          { key: 'pending', label: 'Noch nicht übernommen' },
          { key: 'synced', label: 'Ingame aktuell' },
          { key: 'update_required', label: 'Aktualisierung ausstehend' },
          { key: 'error', label: 'Übernahme fehlgeschlagen' },
          { key: 'delete_requested', label: 'Löschung ausstehend' },
          { key: 'delete_error', label: 'Löschung fehlgeschlagen' },
          { key: 'deleted', label: 'Ingame gelöscht' },
        ],
      },
      { id: 'ingameRevision', label: 'Ingame-Revision', kind: 'number' },
      { id: 'ingameErrorMessage', label: 'Ingame-Fehlermeldung', kind: 'text' },
      { id: 'ingameAssetPath', label: 'Ingame-Asset-Pfad', kind: 'text' },
      { id: 'createdAt', label: 'Erstellt am', kind: 'date' },
      { id: 'updatedAt', label: 'Geändert am', kind: 'date' },
      { id: 'ingameLastSucceededAt', label: 'Letzte erfolgreiche Übernahme', kind: 'date' },
    ];
  }, [reference]);

  const bulkValueOptions = useMemo(() => {
    if (bulkOperation === 'add_tag' || bulkOperation === 'remove_tag') {
      return reference.tagGroups.flatMap((group) => group.tags.map((tag) => ({ key: tag.key, label: `${group.label} › ${tag.label}` })));
    }
    if (bulkOperation === 'add_origin' || bulkOperation === 'remove_origin') return reference.origins;
    if (bulkOperation === 'set_status') return reference.statuses;
    return reference.rarities.map(({ key, label }) => ({ key, label }));
  }, [bulkOperation, reference]);

  useEffect(() => {
    setBulkValue((current) => bulkValueOptions.some((option) => option.key === current) ? current : (bulkValueOptions[0]?.key ?? ''));
  }, [bulkValueOptions]);

  useEffect(() => {
    api.itemListPreference()
      .then((preference) => {
        setPageSize(preference.pageSize);
        setVisibleColumns(preference.visibleColumns?.length ? preference.visibleColumns : [...DEFAULT_ITEM_LIST_COLUMNS]);
      })
      .catch(() => {
        setPageSize(25);
        setVisibleColumns([...DEFAULT_ITEM_LIST_COLUMNS]);
      })
      .finally(() => setPreferencesReady(true));
  }, []);

  useEffect(() => {
    if (!preferencesReady) return;
    const timer = window.setTimeout(() => {
      void api.saveItemListPreference({ pageSize, visibleColumns }).catch((error) => {
        onMessage('error', error instanceof Error ? error.message : 'Die Listenansicht konnte nicht gespeichert werden.');
      });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [preferencesReady, pageSize, visibleColumns]);

  const serializedFilters = useMemo(
    () => appliedFilters.rules.length ? JSON.stringify(appliedFilters) : '',
    [appliedFilters],
  );

  useEffect(() => {
    if (!preferencesReady) return;
    const sequence = ++requestSequence.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const query = new URLSearchParams({ limit: String(pageSize), offset: String(page * pageSize) });
        if (search) query.set('search', search);
        if (status) query.set('status', status);
        if (ingameState) query.set('unrealState', ingameState);
        if (serializedFilters) query.set('filters', serializedFilters);
        const response = await api.items(`?${query}`);
        if (sequence !== requestSequence.current) return;
        setItems(response.items);
        setSelectedIds((current) => new Set([...current].filter((id) => response.items.some((item) => item.id === id))));
        setTotal(response.total);
        setStats(response.stats);
        const lastPage = Math.max(0, Math.ceil(response.total / pageSize) - 1);
        if (page > lastPage) setPage(lastPage);
      } catch (error) {
        if (sequence === requestSequence.current) {
          onMessage('error', error instanceof Error ? error.message : 'Items konnten nicht geladen werden.');
        }
      } finally {
        if (sequence === requestSequence.current) setLoading(false);
      }
    }, 220);
    return () => window.clearTimeout(timer);
  }, [preferencesReady, search, status, ingameState, serializedFilters, page, pageSize, reloadToken]);

  function applyStatus(nextStatus: string) {
    setStatus(nextStatus);
    setIngameState('');
    setPage(0);
  }

  function applyIngameState(nextState: string) {
    setIngameState(nextState);
    setStatus('');
    setPage(0);
  }

  function toggleColumn(column: ItemListColumn) {
    setVisibleColumns((current) => {
      if (current.includes(column)) {
        if (current.length === 1) {
          onMessage('error', 'Mindestens eine Spalte muss sichtbar bleiben.');
          return current;
        }
        return current.filter((entry) => entry !== column);
      }
      return ITEM_LIST_COLUMN_IDS.filter((entry) => current.includes(entry) || entry === column);
    });
  }

  function updateDraftField(id: string, field: ItemFilterField) {
    const definition = filterDefinitions.find((entry) => entry.id === field);
    const operator = operatorsByKind[definition?.kind ?? 'text'][0];
    const value = definition?.options?.[0]?.key ?? '';
    setDraftRules((rules) => rules.map((rule) => rule.id === id ? { id, field, operator, value } : rule));
  }

  function updateDraftOperator(id: string, operator: ItemFilterOperator) {
    setDraftRules((rules) => rules.map((rule) => {
      if (rule.id !== id) return rule;
      return valueFreeOperators.has(operator) ? { ...rule, operator, value: undefined } : { ...rule, operator, value: rule.value ?? '' };
    }));
  }

  function applyAdvancedFilters() {
    for (const rule of draftRules) {
      const definition = filterDefinitions.find((entry) => entry.id === rule.field);
      if (!definition) {
        onMessage('error', 'Ein Filterfeld ist ungültig.');
        return;
      }
      if (!valueFreeOperators.has(rule.operator) && (rule.value === undefined || String(rule.value).trim() === '')) {
        onMessage('error', `Bitte gib für „${definition.label}“ einen Filterwert an.`);
        return;
      }
      if (definition.kind === 'number' && !valueFreeOperators.has(rule.operator) && !Number.isFinite(Number(rule.value))) {
        onMessage('error', `Der Wert für „${definition.label}“ muss eine Zahl sein.`);
        return;
      }
      if (definition.kind === 'date' && !valueFreeOperators.has(rule.operator) && Number.isNaN(Date.parse(String(rule.value)))) {
        onMessage('error', `Das Datum für „${definition.label}“ ist ungültig.`);
        return;
      }
    }

    setAppliedFilters({
      match: draftMatch,
      rules: draftRules.map(({ id: _id, ...rule }) => rule),
    });
    setPage(0);
    setAdvancedOpen(false);
  }

  function clearAdvancedFilters() {
    setDraftRules([]);
    setDraftMatch('all');
    setAppliedFilters({ match: 'all', rules: [] });
    setPage(0);
  }

  function openAdvancedFilters() {
    setDraftMatch(appliedFilters.match);
    setDraftRules(appliedFilters.rules.map((rule) => ({ ...rule, id: crypto.randomUUID() })));
    setAdvancedOpen((open) => !open);
    setColumnsOpen(false);
  }

  function removeAppliedFilter(index: number) {
    const rules = appliedFilters.rules.filter((_rule, ruleIndex) => ruleIndex !== index);
    setAppliedFilters({ ...appliedFilters, rules });
    setDraftRules(rules.map((rule) => ({ ...rule, id: crypto.randomUUID() })));
    setPage(0);
  }

  function valueLabel(rule: ItemFilterRule) {
    if (valueFreeOperators.has(rule.operator)) return '';
    const definition = filterDefinitions.find((entry) => entry.id === rule.field);
    return definition?.options?.find((entry) => entry.key === String(rule.value))?.label ?? String(rule.value ?? '');
  }

  function filterSummary(rule: ItemFilterRule) {
    const field = filterDefinitions.find((entry) => entry.id === rule.field)?.label ?? rule.field;
    const value = valueLabel(rule);
    return `${field} ${operatorLabels[rule.operator]}${value ? ` „${value}“` : ''}`;
  }

  function labelsFor(values: string[], options: { key: string; label: string }[]) {
    return values.map((value) => options.find((entry) => entry.key === value)?.label ?? value);
  }

  function toggleSelected(itemId: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      return next;
    });
  }

  function toggleAllVisible() {
    const visibleIds = items.map((item) => item.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(visibleIds));
  }

  async function applyBulkUpdate() {
    if (selectedIds.size === 0 || !bulkValue) return;
    setBulkBusy(true);
    try {
      const result = await api.bulkUpdateItems({ itemIds: [...selectedIds], operation: bulkOperation, value: bulkValue });
      onMessage('success', `${result.changedCount} Item(s) wurden geändert${result.unchangedCount ? `, ${result.unchangedCount} waren bereits passend` : ''}.`);
      setSelectedIds(new Set());
      setReloadToken((value) => value + 1);
    } catch (error) {
      onMessage('error', error instanceof Error ? error.message : 'Die Stapelbearbeitung ist fehlgeschlagen.');
    } finally {
      setBulkBusy(false);
    }
  }

  function closeImport() {
    if (importBusy) return;
    setImportOpen(false);
    setImportCsv('');
    setImportFileName('');
    setImportPreview(null);
    if (importFileRef.current) importFileRef.current.value = '';
  }

  async function loadImportFile(file: File | null) {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      onMessage('error', 'Bitte wähle eine CSV-Datei aus.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      onMessage('error', 'Die CSV-Datei darf maximal 2 MB groß sein.');
      return;
    }

    setImportBusy(true);
    setImportPreview(null);
    try {
      const csv = await file.text();
      const preview = await api.previewItemBatch(csv);
      setImportCsv(csv);
      setImportFileName(file.name);
      setImportPreview(preview);
    } catch (error) {
      onMessage('error', error instanceof Error ? error.message : 'Die CSV-Datei konnte nicht geprüft werden.');
    } finally {
      setImportBusy(false);
    }
  }

  async function commitImport() {
    if (!importCsv || !importPreview?.canImport) return;
    setImportBusy(true);
    try {
      const result = await api.commitItemBatch(importCsv);
      onMessage('success', `${result.createdCount} Items wurden erfolgreich importiert.`);
      setImportOpen(false);
      setImportCsv('');
      setImportFileName('');
      setImportPreview(null);
      setPage(0);
      setReloadToken((value) => value + 1);
    } catch (error) {
      onMessage('error', error instanceof Error ? error.message : 'Der Batch-Import ist fehlgeschlagen.');
    } finally {
      setImportBusy(false);
    }
  }

  function rarityStyle(item: ItemListEntry) {
    const rarity = reference.rarities.find((entry) => entry.key === item.rarity);
    return { '--rarity-color': rarity?.color ?? '#9AA0A6' } as CSSProperties;
  }

  function renderCell(item: ItemListEntry, column: ItemListColumn) {
    const category = reference.categories.find((entry) => entry.key === item.category);
    const subcategory = category?.subcategories.find((entry) => entry.key === item.subcategory);
    const tags = labelsFor(item.tags, reference.tagGroups.flatMap((group) => group.tags));
    const origins = labelsFor(item.origins, reference.origins);

    switch (column) {
      case 'item':
        return <div className="item-cell"><div className={`thumb rarity-frame rarity-${item.rarity}`} style={rarityStyle(item)}>{item.icon ? <img loading="lazy" decoding="async" src={`${item.icon.thumbnailUrl ?? item.icon.downloadUrl}?v=${item.revision}`}/> : <img src="/assets/potion.svg"/>}</div><div><strong>{item.name}</strong><small>Revision {item.revision}</small></div></div>;
      case 'rarity': {
        const rarity = reference.rarities.find((entry) => entry.key === item.rarity);
        return <span className="rarity-table-label" style={rarityStyle(item)}><i/>{rarity?.label ?? item.rarity}</span>;
      }
      case 'technicalKey': return <code className="table-code">{item.technicalKey}</code>;
      case 'description': return <span className="table-description" title={item.description}>{item.description || '–'}</span>;
      case 'category': return category?.label ?? item.category;
      case 'subcategory': return subcategory?.label ?? item.subcategory;
      case 'contentStatus': return <span className={`status ${item.status}`}>{statusLabel(item.status)}</span>;
      case 'ingameStatus':
        return <div className="unreal-cell"><span className={`sync-state ${item.unrealSync.state}`}>{ingameStatusLabel(item.unrealSync.state)}</span>{(item.unrealSync.state === 'error' || item.unrealSync.state === 'delete_error') && item.unrealSync.errorMessage && <small title={item.unrealSync.errorMessage}>{item.unrealSync.errorMessage}</small>}{item.unrealSync.state === 'update_required' && <small>Ingame R{item.unrealSync.importedRevision} → Studio R{item.revision}</small>}{item.unrealSync.state === 'delete_requested' && <small>Wird über die Content Bridge entfernt</small>}{item.unrealSync.state === 'deleted' && <small>Endgültige Löschung im Studio möglich</small>}</div>;
      case 'tags': return tags.length ? <div className="table-value-list">{tags.map((tag) => <span key={tag}>{tag}</span>)}</div> : '–';
      case 'origins': return origins.length ? origins.join(', ') : '–';
      case 'maximumStack': return formatNumber(item.maximumStack);
      case 'buyPrice': return formatNumber(item.buyPrice);
      case 'sellPrice': return formatNumber(item.sellPrice);
      case 'revision': return item.revision;
      case 'iconStatus': return item.icon ? <span className="availability yes">Vorhanden</span> : <span className="availability no">Fehlt</span>;
      case 'updatedAt': return formatDate(item.updatedAt);
      case 'createdAt': return formatDate(item.createdAt);
      case 'ingameRevision': return item.unrealSync.importedRevision || '–';
      case 'ingameAssetPath': return item.unrealSync.assetPath ? <code className="table-code" title={item.unrealSync.assetPath}>{item.unrealSync.assetPath}</code> : '–';
      case 'ingameLastSucceededAt': return item.unrealSync.lastSucceededAt ? formatDate(item.unrealSync.lastSucceededAt) : '–';
    }
  }

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const firstShown = total === 0 ? 0 : page * pageSize + 1;
  const lastShown = Math.min(total, (page + 1) * pageSize);
  const hasAnyFilter = Boolean(search || status || ingameState || appliedFilters.rules.length);

  return <>
    <div className="page-heading">
      <div><span className="eyebrow">Inhaltskatalog</span><h1>Items</h1><p>Das Content Studio ist die führende Quelle. Das Spiel übernimmt ausschließlich freigegebene Revisionen über die Bridge.</p></div>
      <div className="heading-actions item-heading-actions">
        <button className="button secondary" onClick={() => window.location.assign(api.itemBatchExampleUrl)}>Beispiel-CSV</button>
        <button className="button secondary" onClick={() => setImportOpen(true)}>Batch-Import</button>
        <button className="button secondary" onClick={() => window.location.assign('/api/v1/exports/items.zip')}><img src="/assets/exports.svg"/>Datenexport</button>
        <button className="button primary" onClick={onNew}><img src="/assets/plus.svg"/>Neues Item</button>
      </div>
    </div>

    <section className="stat-grid sync-stat-grid">
      <button className={`stat-card filter-stat ${status === '' && ingameState === '' ? 'active' : ''}`} onClick={() => { setStatus(''); setIngameState(''); setPage(0); }} aria-pressed={status === '' && ingameState === ''}><span>Alle Items</span><strong>{stats.loaded}</strong></button>
      <button className={`stat-card filter-stat ${status === 'approved' ? 'active' : ''}`} onClick={() => applyStatus('approved')} aria-pressed={status === 'approved'}><span>Fürs Spiel freigegeben</span><strong>{stats.approved}</strong></button>
      <button className={`stat-card filter-stat ${status === 'review' ? 'active' : ''}`} onClick={() => applyStatus('review')} aria-pressed={status === 'review'}><span>Zur Prüfung</span><strong>{stats.review}</strong></button>
      <button className={`stat-card filter-stat error ${ingameState === 'error' ? 'active' : ''}`} onClick={() => applyIngameState('error')} aria-pressed={ingameState === 'error'}><span>Übernahme fehlgeschlagen</span><strong>{stats.errors}</strong></button>
      <button className={`stat-card filter-stat warning ${ingameState === 'update_required' ? 'active' : ''}`} onClick={() => applyIngameState('update_required')} aria-pressed={ingameState === 'update_required'}><span>Aktualisierung ausstehend</span><strong>{stats.updateRequired}</strong></button>
      <button className={`stat-card filter-stat deletion ${ingameState === 'deletion_attention' ? 'active' : ''}`} onClick={() => applyIngameState('deletion_attention')} aria-pressed={ingameState === 'deletion_attention'}><span>Löschvorgänge</span><strong>{stats.deletionPending + stats.deletionErrors}</strong><small>{stats.deletionErrors ? `${stats.deletionErrors} Fehler` : 'Keine Fehler'}</small></button>
    </section>

    <section className="panel list-panel advanced-list-panel">
      <div className="item-list-toolbar">
        <label className="search-field"><img src="/assets/search.svg"/><input placeholder="Name, Beschreibung oder Schlüssel durchsuchen …" value={search} onChange={(event) => { setSearch(event.target.value); setPage(0); }}/></label>
        <button className={`button secondary ${appliedFilters.rules.length ? 'has-count' : ''}`} onClick={openAdvancedFilters}>Erweiterte Filter{appliedFilters.rules.length > 0 && <span className="button-count">{appliedFilters.rules.length}</span>}</button>
        <button className="button secondary" onClick={() => { setColumnsOpen((open) => !open); setAdvancedOpen(false); }}>Spalten <span className="button-count neutral">{visibleColumns.length}</span></button>
        {hasAnyFilter && <button className="button ghost" onClick={() => { setSearch(''); setStatus(''); setIngameState(''); clearAdvancedFilters(); }}>Alle Filter löschen</button>}
      </div>

      {selectedIds.size > 0 && <div className="bulk-action-bar">
        <div className="bulk-selection-summary"><strong>{selectedIds.size}</strong><span>Item(s) ausgewählt</span><button className="button ghost small" onClick={() => setSelectedIds(new Set())}>Auswahl aufheben</button></div>
        <label>Aktion<select value={bulkOperation} onChange={(event) => setBulkOperation(event.target.value as ItemBulkOperation)} disabled={bulkBusy}><option value="add_tag">Tag hinzufügen</option><option value="remove_tag">Tag entfernen</option><option value="add_origin">Herkunft hinzufügen</option><option value="remove_origin">Herkunft entfernen</option><option value="set_status">Freigabestatus setzen</option><option value="set_rarity">Seltenheit setzen</option></select></label>
        <label>Wert<select value={bulkValue} onChange={(event) => setBulkValue(event.target.value)} disabled={bulkBusy}>{bulkValueOptions.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}</select></label>
        <button className="button primary" disabled={bulkBusy || !bulkValue} onClick={() => void applyBulkUpdate()}>{bulkBusy ? 'Wird angewendet …' : 'Auf Auswahl anwenden'}</button>
      </div>}

      {advancedOpen && <div className="configuration-panel advanced-filter-panel">
        <div className="configuration-heading"><div><h3>Erweiterte Filter</h3><p>Filtere auf sämtliche Itemdaten. Mehrere Bedingungen können gemeinsam oder alternativ gelten.</p></div><button className="button ghost small" onClick={() => setAdvancedOpen(false)}>Schließen</button></div>
        <div className="filter-match-row"><label>Bedingungen verknüpfen<select value={draftMatch} onChange={(event) => setDraftMatch(event.target.value as ItemFilterSet['match'])}><option value="all">Alle Bedingungen müssen zutreffen</option><option value="any">Mindestens eine Bedingung muss zutreffen</option></select></label><button className="button secondary" onClick={() => setDraftRules((rules) => [...rules, makeRule('name', filterDefinitions)])}>+ Bedingung hinzufügen</button></div>
        <div className="advanced-filter-rules">
          {draftRules.length === 0 && <div className="filter-empty-state">Noch keine Bedingungen. Beispiele: <strong>Tag enthält „Magisch“</strong>, <strong>Verkaufspreis größer als 50</strong> oder <strong>Beschreibung ist leer</strong>.</div>}
          {draftRules.map((rule) => {
            const definition = filterDefinitions.find((entry) => entry.id === rule.field) ?? filterDefinitions[0];
            const operators = operatorsByKind[definition.kind];
            const needsValue = !valueFreeOperators.has(rule.operator);
            return <div className="advanced-filter-row" key={rule.id}>
              <label>Feld<select value={rule.field} onChange={(event) => updateDraftField(rule.id, event.target.value as ItemFilterField)}>{filterDefinitions.map((entry) => <option value={entry.id} key={entry.id}>{entry.label}</option>)}</select></label>
              <label>Operator<select value={rule.operator} onChange={(event) => updateDraftOperator(rule.id, event.target.value as ItemFilterOperator)}>{operators.map((operator) => <option value={operator} key={operator}>{operatorLabels[operator]}</option>)}</select></label>
              <label className="filter-value">Wert{!needsValue ? <div className="operator-no-value">Kein Wert erforderlich</div> : definition.options ? <select value={String(rule.value ?? '')} onChange={(event) => setDraftRules((rules) => rules.map((entry) => entry.id === rule.id ? { ...entry, value: event.target.value } : entry))}>{definition.options.map((option) => <option value={option.key} key={option.key}>{option.label}</option>)}</select> : definition.kind === 'number' ? <input type="number" value={String(rule.value ?? '')} onChange={(event) => setDraftRules((rules) => rules.map((entry) => entry.id === rule.id ? { ...entry, value: event.target.value } : entry))}/> : definition.kind === 'date' ? <input type="datetime-local" value={String(rule.value ?? '')} onChange={(event) => setDraftRules((rules) => rules.map((entry) => entry.id === rule.id ? { ...entry, value: event.target.value } : entry))}/> : <input value={String(rule.value ?? '')} onChange={(event) => setDraftRules((rules) => rules.map((entry) => entry.id === rule.id ? { ...entry, value: event.target.value } : entry))}/>}</label>
              <button className="remove-filter-button" aria-label="Filterbedingung entfernen" onClick={() => setDraftRules((rules) => rules.filter((entry) => entry.id !== rule.id))}>×</button>
            </div>;
          })}
        </div>
        <div className="configuration-actions"><button className="button ghost" onClick={() => { setDraftRules([]); setDraftMatch('all'); }}>Entwurf leeren</button><button className="button primary" onClick={applyAdvancedFilters}>Filter anwenden</button></div>
      </div>}

      {columnsOpen && <div className="configuration-panel column-panel">
        <div className="configuration-heading"><div><h3>Spalten auswählen</h3><p>Die Auswahl wird automatisch für deinen Benutzer gespeichert.</p></div><button className="button ghost small" onClick={() => setColumnsOpen(false)}>Schließen</button></div>
        <div className="column-grid">{ITEM_LIST_COLUMN_IDS.map((column) => <label className={`column-option ${visibleColumns.includes(column) ? 'selected' : ''}`} key={column}><input type="checkbox" checked={visibleColumns.includes(column)} onChange={() => toggleColumn(column)}/><span>{columnLabels[column]}</span></label>)}</div>
        <div className="configuration-actions"><button className="button ghost" onClick={() => setVisibleColumns([...DEFAULT_ITEM_LIST_COLUMNS])}>Standard wiederherstellen</button><button className="button primary" onClick={() => setColumnsOpen(false)}>Fertig</button></div>
      </div>}

      {appliedFilters.rules.length > 0 && <div className="active-filter-chips"><span>Aktive Filter:</span>{appliedFilters.rules.map((rule, index) => <button key={`${rule.field}-${index}`} title="Filter entfernen" onClick={() => removeAppliedFilter(index)}>{filterSummary(rule)} <strong>×</strong></button>)}</div>}

      <div className="table-wrap"><table className="customizable-item-table selectable-item-table" style={{ minWidth: `${Math.max(980, visibleColumns.length * 155 + 150)}px` }}><thead><tr><th className="selection-column"><input type="checkbox" aria-label="Alle sichtbaren Items auswählen" checked={items.length > 0 && items.every((item) => selectedIds.has(item.id))} onChange={toggleAllVisible}/></th>{visibleColumns.map((column) => <th key={column}>{columnLabels[column]}</th>)}<th className="row-actions-column">Aktionen</th></tr></thead><tbody>
        {!loading && items.map((item) => <tr key={item.id} className={selectedIds.has(item.id) ? 'selected-row' : ''} onClick={() => onOpen(item.id)} tabIndex={0} onKeyDown={(event) => { if (event.key === 'Enter') onOpen(item.id); }}><td className="selection-column" onClick={(event) => event.stopPropagation()}><input type="checkbox" aria-label={`${item.name} auswählen`} checked={selectedIds.has(item.id)} onChange={() => toggleSelected(item.id)}/></td>{visibleColumns.map((column) => <td key={column} className={`column-${column}`}>{renderCell(item, column)}</td>)}<td className="row-actions-column" onClick={(event) => event.stopPropagation()}><button className="button ghost small" onClick={() => onDuplicate(item.id)} disabled={item.unrealSync.state === 'delete_requested' || item.unrealSync.state === 'delete_error' || item.unrealSync.state === 'deleted'}>Duplizieren</button></td></tr>)}
      </tbody></table></div>

      {loading && <div className="empty">Items werden geladen …</div>}
      {!loading && items.length === 0 && <div className="empty"><img src="/assets/chest.svg"/><h3>Keine Items gefunden</h3><p>Ändere die Filter oder lege das erste Item an.</p></div>}

      <div className="item-pagination">
        <div><strong>{firstShown}–{lastShown}</strong><span> von {total} Items</span></div>
        <label>Pro Seite<select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value) as ItemListPreference['pageSize']); setPage(0); }}>{pageSizes.map((size) => <option key={size} value={size}>{size}</option>)}</select></label>
        <div className="pagination-buttons"><button className="button ghost small" disabled={page <= 0 || loading} onClick={() => setPage((value) => Math.max(0, value - 1))}>← Zurück</button><span>Seite {page + 1} von {pageCount}</span><button className="button ghost small" disabled={page + 1 >= pageCount || loading} onClick={() => setPage((value) => Math.min(pageCount - 1, value + 1))}>Weiter →</button></div>
      </div>
    </section>

    {importOpen && <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeImport(); }}>
      <section className="panel batch-import-modal" role="dialog" aria-modal="true" aria-labelledby="batch-import-title">
        <div className="configuration-heading">
          <div><span className="eyebrow">CSV-Werkzeug</span><h2 id="batch-import-title">Items per Batch importieren</h2><p>Lade eine mit der Beispiel-CSV aufgebaute Datei hoch. IDs und automatisch erzeugte technische Schlüssel werden erst beim endgültigen Import gespeichert.</p></div>
          <button className="button ghost small" onClick={closeImport} disabled={importBusy}>Schließen</button>
        </div>

        <div className="batch-import-dropzone">
          <input ref={importFileRef} type="file" accept=".csv,text/csv" hidden onChange={(event) => void loadImportFile(event.target.files?.[0] ?? null)}/>
          <button className="button secondary" onClick={() => importFileRef.current?.click()} disabled={importBusy}>{importFileName ? 'Andere CSV auswählen' : 'CSV-Datei auswählen'}</button>
          <button className="button ghost" onClick={() => window.location.assign(api.itemBatchExampleUrl)}>Beispiel-CSV herunterladen</button>
          <span>{importFileName || 'Noch keine Datei ausgewählt'}</span>
        </div>

        {importBusy && <div className="batch-import-loading">CSV wird geprüft …</div>}

        {importPreview && <>
          <div className="batch-import-summary">
            <div><span>Zeilen</span><strong>{importPreview.totalCount}</strong></div>
            <div className="success"><span>Importbereit</span><strong>{importPreview.validCount}</strong></div>
            <div className={importPreview.invalidCount ? 'error' : ''}><span>Fehlerhaft</span><strong>{importPreview.invalidCount}</strong></div>
          </div>
          <div className="batch-preview-table-wrap"><table className="batch-preview-table"><thead><tr><th>Zeile</th><th>Item</th><th>Technischer Schlüssel</th><th>Prüfung</th></tr></thead><tbody>{importPreview.rows.map((row) => <tr key={row.rowNumber} className={row.valid ? 'valid' : 'invalid'}><td>{row.rowNumber}</td><td>{row.name}</td><td><code>{row.technicalKey || '–'}</code></td><td>{row.valid ? <span className="availability yes">Bereit</span> : <ul>{row.errors.map((error) => <li key={error}>{error}</li>)}</ul>}</td></tr>)}</tbody></table></div>
        </>}

        <div className="configuration-actions">
          <button className="button ghost" onClick={closeImport} disabled={importBusy}>Abbrechen</button>
          <button className="button primary" onClick={() => void commitImport()} disabled={importBusy || !importPreview?.canImport}>{importBusy ? 'Importiert …' : `${importPreview?.validCount ?? 0} Items importieren`}</button>
        </div>
      </section>
    </div>}
  </>;
}
